import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { debounce, makeCancellablePromise } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { MIN_SEARCH_LENGTH } from 'APP/constants/search';
import { GroupUserRole } from 'APP/model/group/groupModel.types';

import { SEARCH_DELAY, PARTICIPANT_LIMIT } from './ParticipantList.constants';
import type {
  IParticipantListPresenterParams,
  IParticipantListPresenter,
} from './ParticipantList.types';

export function useParticipantListPresenter({
  group,
  searchText,
  onAddParticipantClick,
}: IParticipantListPresenterParams): IParticipantListPresenter {
  const presenter = useLocalObservable<IParticipantListPresenter>(() => ({
    group,
    searchText: '',
    cursor: null,
    isLoading: false,
    participants: [],
    cancelLoadMore: null,

    get canAddParticipants(): boolean {
      return presenter.group?.canInviteUsers || false;
    },

    get hasMore(): boolean {
      return !!presenter.cursor;
    },

    get isSearch(): boolean {
      return presenter.searchText.length >= MIN_SEARCH_LENGTH;
    },

    async init(): Promise<void> {
      presenter.resetSearch();
      await presenter.loadMore();
    },

    resetSearch(): void {
      presenter.participants = [];
      presenter.cursor = null;
      presenter.isLoading = false;
    },

    search: (): void => {
      if (!presenter.isSearch) {
        return;
      }

      presenter.resetSearch();
      presenter.loadParticipants();
    },

    loadParticipants: debounce(async (): Promise<void> => {
      await presenter.loadMore();
    }, SEARCH_DELAY),

    async loadMore(): Promise<void> {
      if (!presenter.group) {
        return;
      }

      if (presenter.cancelLoadMore) {
        presenter.cancelLoadMore();
      }

      presenter.isLoading = true;

      const { promise: getParticipants, cancel } = makeCancellablePromise(
        Tasks.group.getParticipants({
          groupId: presenter.group.id,
          searchText: this.isSearch ? presenter.searchText : '',
          cursor: presenter.cursor || '',
          limit: PARTICIPANT_LIMIT,
        })
      );

      presenter.cancelLoadMore = cancel;

      const groupParticipantsData = await getParticipants;

      if (groupParticipantsData) {
        const { participants, cursor } = groupParticipantsData;
        presenter.participants.push(...participants);
        presenter.cursor = cursor;
      }

      presenter.cancelLoadMore = null;
      presenter.isLoading = false;
    },

    removeParticipant(userId: string): void {
      presenter.participants = presenter.participants.filter(
        (participant) => participant.user.id !== userId
      );
    },

    updateParticipantRole(userId: string, role: GroupUserRole): void {
      const participant = presenter.participants.find(
        (participant) => participant.user.id === userId
      );
      if (participant) {
        participant.role = role;
      }
    },

    showAddPeople(): void {
      const onSuccess = (): void => {
        searchText ? presenter.search() : presenter.init();
      };

      onAddParticipantClick(onSuccess);
    },

    onReloadParticipantsList(): void {
      presenter.init();
    },
  }));

  useEffect(() => {
    presenter.group = group;
  }, [group]);

  useEffect(() => {
    if (!presenter.group?.userGroupRole) {
      return;
    }
    presenter.isSearch ? presenter.search() : presenter.onReloadParticipantsList();
  }, [presenter.group?.userGroupRole]);

  useEffect(() => {
    if (presenter.group && !presenter.isSearch) {
      presenter.init();
    }
  }, [presenter.group, presenter.isSearch]);

  useEffect(() => {
    presenter.searchText = searchText;
    if (presenter.group) {
      presenter.search();
    }
  }, [searchText, presenter.group]);

  return presenter;
}
